<template>
  <div class="offers-container">
    <div class="content-wrapper">
      <div class="header-section">
        <img
          class="logo"
          src="@website-assets/img/logo-stowy.png"
          alt="Stowy Logo"
          @click="goHome"
        />
      </div>

      <div class="main-content">
        <h1 class="title">{{ $t('exclusiveOffer.offers-title') }}</h1>
        <h2 class="subtitle">{{ $t('exclusiveOffer.offers-subtitle') }}</h2>
        <p class="intro-text">{{ $t('exclusiveOffer.offers-intro') }}</p>

        <!-- Loading state -->
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>{{ $t('exclusiveOffer.offers-loading') }}</p>
        </div>

        <!-- Offers Grid -->
        <div v-else class="plans__list">
          <!-- Solo Discount (-50%) -->
          <article class="plan plan-blue" v-if="offers['solo-discount']">
            <div class="plan__badge plan__badge--discount">-50%</div>
            <div class="plan__wrapper">
              <div class="plan__price">{{ $t('exclusiveOffer.offer-solo-discount-name') }}</div>
              <div class="plan__price-wrapper">
                <h3 class="plan__title">{{ formatPrice(offers['solo-discount'].price) }}</h3>
                <div class="plan__price-info">{{ $t('home.plan-solo-6') }}</div>
                <div class="plan__price-info plan__price-info--strike">
                  {{ $t('exclusiveOffer.original-price') }}: 4,99€/mois
                </div>
              </div>
              <ul class="plan__offer">
                <li>{{ $t('home.plan-solo-2') }}</li>
                <li>{{ $t('home.plan-solo-3') }}</li>
                <li class="not-included">{{ $t('home.plan-solo-4') }}</li>
              </ul>
              <footer class="choose-plan-btn">
                <button
                  type="button"
                  @click="selectPlan('solo-discount')"
                  class="btn btn-primary btn-L"
                  :disabled="isProcessing"
                >
                  <span>{{ $t('global.choose-formula') }}</span>
                </button>
              </footer>
            </div>
          </article>

          <!-- Multi Discount (-50%) -->
          <article class="plan plan-orange" v-if="offers['multi-discount']">
            <div class="plan__badge plan__badge--discount">-50%</div>
            <div class="plan__wrapper">
              <div class="plan__price">{{ $t('exclusiveOffer.offer-multi-discount-name') }}</div>
              <div class="plan__price-wrapper">
                <h3 class="plan__title">{{ formatPrice(offers['multi-discount'].price) }}</h3>
                <div class="plan__price-info">{{ $t('home.plan-solo-6') }}</div>
                <div class="plan__price-info plan__price-info--strike">
                  {{ $t('exclusiveOffer.original-price') }}: 6,99€/mois
                </div>
              </div>
              <ul class="plan__offer">
                <li>{{ $t('home.plan-family-2') }}</li>
                <li>{{ $t('home.plan-family-3') }}</li>
                <li>{{ $t('home.plan-family-4') }}</li>
              </ul>
              <footer class="choose-plan-btn">
                <button
                  type="button"
                  @click="selectPlan('multi-discount')"
                  class="btn btn-secondary-orange btn-L"
                  :disabled="isProcessing"
                >
                  <span>{{ $t('global.choose-formula') }}</span>
                </button>
              </footer>
            </div>
          </article>

          <!-- Solo Lifetime -->
          <article class="plan plan-purple" v-if="offers['solo-lifetime']">
            <div class="plan__badge plan__badge--lifetime">{{ $t('exclusiveOffer.lifetime-badge') }}</div>
            <div class="plan__wrapper">
              <div class="plan__price">{{ $t('exclusiveOffer.offer-solo-lifetime-name') }}</div>
              <div class="plan__price-wrapper">
                <h3 class="plan__title">{{ formatPrice(offers['solo-lifetime'].price) }}</h3>
                <div class="plan__price-info">{{ $t('exclusiveOffer.one-time-payment') }}</div>
                <div class="plan__price-info">{{ $t('exclusiveOffer.lifetime-access') }}</div>
              </div>
              <ul class="plan__offer">
                <li>{{ $t('home.plan-solo-2') }}</li>
                <li>{{ $t('home.plan-solo-3') }}</li>
                <li class="not-included">{{ $t('home.plan-solo-4') }}</li>
              </ul>
              <footer class="choose-plan-btn">
                <button
                  type="button"
                  @click="selectPlan('solo-lifetime')"
                  class="btn btn-secondary btn-L"
                  :disabled="isProcessing"
                >
                  <span>{{ $t('global.choose-formula') }}</span>
                </button>
              </footer>
            </div>
          </article>

          <!-- Multi Lifetime -->
          <article class="plan plan-green" v-if="offers['multi-lifetime']">
            <div class="plan__badge plan__badge--lifetime">{{ $t('exclusiveOffer.lifetime-badge') }}</div>
            <div class="plan__wrapper">
              <div class="plan__price">{{ $t('exclusiveOffer.offer-multi-lifetime-name') }}</div>
              <div class="plan__price-wrapper">
                <h3 class="plan__title">{{ formatPrice(offers['multi-lifetime'].price) }}</h3>
                <div class="plan__price-info">{{ $t('exclusiveOffer.one-time-payment') }}</div>
                <div class="plan__price-info">{{ $t('exclusiveOffer.lifetime-access') }}</div>
              </div>
              <ul class="plan__offer">
                <li>{{ $t('home.plan-family-2') }}</li>
                <li>{{ $t('home.plan-family-3') }}</li>
                <li>{{ $t('home.plan-family-4') }}</li>
              </ul>
              <footer class="choose-plan-btn">
                <button
                  type="button"
                  @click="selectPlan('multi-lifetime')"
                  class="btn btn-secondary btn-L"
                  :disabled="isProcessing"
                >
                  <span>{{ $t('global.choose-formula') }}</span>
                </button>
              </footer>
            </div>
          </article>
        </div>

        <div class="info-section" v-if="!loading">
          <p class="info-text">{{ $t('exclusiveOffer.offers-note') }}</p>
        </div>

        <div class="cta-section">
          <button
            class="btn btn-secondary btn-L"
            @click="goHome"
          >
            {{ $t('exclusiveOffer.offers-back') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="showBlockerModal"
    class="discount-modal"
    @click.self="closeBlocker"
  >
    <div class="discount-modal__card">
      <p class="discount-modal__message">
        {{ $t('exclusiveOffer.discount-not-available') }}
      </p>
      <button class="btn btn-primary btn-L" @click="redirectToPlan">
        {{ $t('exclusiveOffer.offers-back') }}
      </button>
    </div>
  </div>
</template>

<script>
import { getExclusiveOffersPrices, formatPrice as formatPriceUtil } from '@common/stripeService';
import { auth } from '@common/firebase';

export default {
  name: 'Offers',
  data() {
    return {
      offers: {},
      loading: true,
      isProcessing: false,
      showBlockerModal: false
    };
  },
  computed: {
    hasActiveSubscription() {
      const role = this.$store.state.userSession?.profile?.stripeRole
      return !!(role && role !== 'free')
    }
  },
  async mounted() {
    try {
      const offersArray = await getExclusiveOffersPrices();

      // Convert array to object for easier access
      this.offers = offersArray.reduce((acc, offer) => {
        acc[offer.key] = offer;
        return acc;
      }, {});
    } catch (error) {
      console.error('Error loading offers:', error);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    goHome() {
      this.$router.push('/');
    },
    formatPrice(priceInfo) {
      return formatPriceUtil(priceInfo, this.$i18n.locale === 'en' ? 'en-US' : 'fr-FR');
    },
    selectPlan(planKey) {
      const isExclusiveOffer = planKey !== 'free'

      if (this.hasActiveSubscription && isExclusiveOffer) {
        this.showBlockerModal = true
        return
      }

      // Store selected offer in Vuex and localStorage
      this.$store.commit('setSelectedOffer', planKey);

      // Check if user is logged in
      const user = auth.currentUser;
      const isAnonymous = user?.isAnonymous || false;

      // If user is not logged in or is anonymous, redirect to register page
      if (!user || isAnonymous) {
        window.location.href = `/account/register`;
        return;
      }

      // User is logged in (real user), redirect to account
      // The account module will handle the checkout
      window.location.href = `/account`;
    },
    closeBlocker() {
      this.showBlockerModal = false
    },
    redirectToPlan() {
      this.showBlockerModal = false
      window.location.href = '/account/plan'
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../style/vars/index";
@import "../style/common/plans";

.offers-container {
  min-height: 100vh;
  background: linear-gradient(180deg, #f8f9ff 0%, #ffffff 100%);
  padding: 2rem;
}

.content-wrapper {
  max-width: 1400px;
  margin: 0 auto;
}

.header-section {
  text-align: center;
  margin-bottom: 2rem;

  .logo {
    width: min(200px, 40vw);
    cursor: pointer;
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.05);
    }
  }
}

.main-content {
  background: white;
  border-radius: 30px;
  padding: min(4rem, 8vw);
  box-shadow: 0px 10px 40px rgba(77, 160, 255, 0.15);
  text-align: center;
}

.coming-soon-badge {
  display: inline-block;
  background: linear-gradient(90deg, #4DA0FF 0%, #7B4FD4 100%);
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 25px;
  font-family: "Baloo 2", sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 2rem;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.title {
  font-family: "Baloo 2", sans-serif;
  font-size: min(3rem, 8vw);
  font-weight: 700;
  color: #4da0ff;
  margin-bottom: 1rem;
  line-height: 1.2;
}

.subtitle {
  font-family: "Baloo 2", sans-serif;
  font-size: min(1.8rem, 5vw);
  font-weight: 600;
  background: linear-gradient(90deg, #4DA0FF 0%, #7B4FD4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 3rem;
}

.placeholder-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
}

.offer-card-placeholder {
  position: relative;
  background: #f8f9ff;
  border-radius: 20px;
  padding: 2rem;
  min-height: 200px;
  overflow: hidden;

  .card-shimmer {
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.5) 50%,
      transparent 100%
    );
    animation: shimmer 2s infinite;
  }

  .card-content {
    position: relative;
    z-index: 2;
  }

  .offer-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  .offer-text {
    font-family: "Baloo 2", sans-serif;
    color: #666;
    font-size: 1rem;
    line-height: 1.5;
  }
}

@keyframes shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

.info-section {
  margin-bottom: 3rem;

  .info-text {
    font-family: "Baloo 2", sans-serif;
    font-size: min(1.2rem, 4vw);
    color: #666;
    line-height: 1.6;
  }
}

.cta-section {
  .btn {
    font-size: min(1.5rem, 5vw);
    padding: 1.2rem 2rem;
    width: 100%;
    max-width: 300px;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1.4;
  }

  @media (min-width: 768px) {
    .btn {
      font-size: 1.5rem;
    }
  }
}

// Loading state
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;

  .spinner {
    width: 60px;
    height: 60px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #4da0ff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1.5rem;
  }

  p {
    font-family: "Baloo 2", sans-serif;
    font-size: 1.2rem;
    color: #666;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

// Badge styles
.plan__badge {
  position: absolute;
  top: -15px;
  right: 20px;
  padding: 8px 20px;
  border-radius: 25px;
  font-family: "Baloo 2", sans-serif;
  font-weight: 700;
  font-size: 1rem;
  line-height: 1;
  z-index: 10;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &--discount {
    background: linear-gradient(135deg, #ff6b6b 0%, #ff5252 100%);
    color: white;
    box-shadow: 0 4px 15px rgba(255, 107, 107, 0.4);
  }

  &--lifetime {
    background: linear-gradient(135deg, #ffd700 0%, #ffb700 100%);
    color: #333;
    box-shadow: 0 4px 15px rgba(255, 215, 0, 0.4);
  }

  @media (max-width: 768px) {
    top: -10px;
    right: 10px;
    padding: 6px 16px;
    font-size: 0.85rem;
  }
}

// Price info styles
.plan__price-info {
  &--strike {
    text-decoration: line-through;
    opacity: 0.6;
    font-size: 0.9rem;
    margin-top: 0.5rem;
  }
}

// Green plan color (for multi-lifetime)
.plan-green {
  .plan__wrapper {
    background: linear-gradient(180deg, #ffffff 0%, #f0fff4 100%);
    border: 2px solid #48bb78;
  }

  .plan__title {
    color: #48bb78;
  }

  .btn {
    background: #48bb78;

    &:hover {
      background: #38a169;
    }
  }
}

// Plans list grid
.plans__list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin: 3rem 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
}

.intro-text {
  font-family: "Baloo 2", sans-serif;
  font-size: min(1.2rem, 4vw);
  color: #666;
  line-height: 1.6;
  margin-bottom: 2rem;
}

@media (max-width: 768px) {
  .offers-container {
    padding: 1rem;
  }

  .main-content {
    padding: 2rem 1.5rem;
  }

  .placeholder-content {
    grid-template-columns: 1fr;
  }
}

.discount-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  padding: 1.5rem;

  &__card {
    background: #fff;
    border-radius: 24px;
    max-width: 420px;
    width: 100%;
    padding: 2rem;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
    text-align: center;
  }

  &__message {
    font-family: "Baloo 2", sans-serif;
    font-size: 1.2rem;
    color: #444;
    margin-bottom: 1.5rem;
    line-height: 1.5;
  }
}
</style>
